import { Matrix, MatrixItem } from 'common/components/Matrix';
import ImageTile from 'launch/components/ImageTile';
import Mediabox from 'launch/components/Mediabox';
import * as React from 'react';
import CreateContext from './CreateContext';

export type TimeframeValue =
    | 'FORTY_EIGHT_HOURS'
    | 'NOT_SURE'
    | 'ONE_MONTH'
    | 'ONE_WEEK'
    | 'SPECIFIC_DATES'
    | 'TWO_WEEKS';

export interface Props {
    value: TimeframeValue | null;
    onChange: (value: TimeframeValue) => void;
}

export interface SummaryProps {
    value: TimeframeValue;
    startDate: string;
    endDate: string;
}

export function TimeframeSummary({ value, startDate, endDate }: SummaryProps) {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const { locale } = React.useContext(CreateContext);

    function message() {
        switch (value) {
            case 'NOT_SURE':
                return __("Not sure. I'd like to chat with my designer");
            case 'FORTY_EIGHT_HOURS':
                return __('48 hours');
            case 'ONE_MONTH':
                return __('1 month');
            case 'ONE_WEEK':
                return __('1 week');
            case 'TWO_WEEKS':
                return __('2 weeks');
            case 'SPECIFIC_DATES':
                if (startDate && endDate) {
                    return (
                        new Intl.DateTimeFormat(locale, options).format(new Date(startDate)) +
                        ' - ' +
                        new Intl.DateTimeFormat(locale, options).format(new Date(endDate))
                    );
                }
            default:
                return __("Not sure. I'd like to chat with my designer");
        }
    }

    return <>{message()}</>;
}

interface TimeFrameOption {
    key: TimeframeValue;
    url: string;
    title: string;
    label: string;
}

export default class TimeFrame extends React.Component<Props> {
    render() {
        const imagePath = function(fileName: string) {
            return require(`images/projects/create/${fileName}`);
        };

        const options: TimeFrameOption[] = [
            {
                key: 'FORTY_EIGHT_HOURS',
                url: 'calendar_hour.svg',
                title: __('48 hours'),
                label: __('Quick turnaround fees typically apply'),
            },
            {
                key: 'ONE_WEEK',
                url: 'calendar_1week.svg',
                title: __('1 week'),
                label: '',
            },
            {
                key: 'TWO_WEEKS',
                url: 'calendar_2weeks.svg',
                title: __('2 weeks'),
                label: '',
            },
            {
                key: 'ONE_MONTH',
                url: 'calendar_month.svg',
                title: __('1 month'),
                label: '',
            },
            {
                key: 'SPECIFIC_DATES',
                url: 'calendar_date.svg',
                title: __('Choose a start & end date'),
                label: '',
            },
            {
                key: 'NOT_SURE',
                url: 'calendar_question.svg',
                title: __("Not sure. I'd like to chat with my designer"),
                label: '',
            },
        ];

        const optionItems = options.map(option => (
            <MatrixItem key={option.key}>
                <Mediabox
                    variations={['linked', 'chameleon']}
                    selected={this.props.value == option.key}
                    onClick={() => this.props.onChange(option.key)}
                >
                    <ImageTile
                        imagePadding={true}
                        url={imagePath(option.url)}
                        title={option.title}
                        label={option.label}
                    />
                </Mediabox>
            </MatrixItem>
        ));

        return (
            <Matrix large={3} medium={2} small={1} staggered={false}>
                {optionItems}
            </Matrix>
        );
    }
}
